/* product-showcase v4 — PetiteVue reactive cards, no innerHTML */

// Global touch flag: flips to true on the first touchstart anywhere in the
// document. Click handlers on swatches use this to decide whether to navigate
// (mouse) or just update the card state (touch). Exposed on window so inline
// handlers in the static Liquid snippet can read it.
window.showcaseTouchDetected = false
window.addEventListener(
  'touchstart',
  () => {
    window.showcaseTouchDetected = true
  },
  { passive: true, once: true }
)

createComponent('ProductShowcase', function (sectionId) {
  const script = document.querySelector(
    `script[data-showcase-config][data-section-id="${sectionId}"]`
  )
  if (!script) {
    console.error('[ProductShowcase] config script not found for section', sectionId)
    return {}
  }

  let config
  try {
    config = JSON.parse(script.textContent)
  } catch (e) {
    console.error('[ProductShowcase] failed to parse config', e)
    return {}
  }

  const product = ProductState({
    ...config.product,
    isPDP: false,
  })
  if (product.isSimple) product.bundles = []
  product.isSimple = !product.bundles.length

  const needsHydration = !product.variants?.length
  // Per-instance flag (closure) so v-effect="init()" — which calls init with
  // global `this` — does not leak state between independent showcases.
  let initialized = false
  const defaultMaxVisible = config.maxSwatchesToDisplay || 6

  // Compute cards synchronously when the product already has variants, so
  // <template v-for="(card, index) in cards"> has items on first render.
  // Otherwise petite-vue removes the template (zero-iteration) and the
  // subsequent re-render crashes with "Cannot read properties of null
  // (reading 'insertBefore')" because the template's parent reference is gone.
  const initialCards = !needsHydration
    ? computeCards(product, config.blocks || [], defaultMaxVisible, config.sourceDiscountBadge || '')
    : []

  return {
    product,
    cards: initialCards,
    isHydrated: !needsHydration,
    hoverImage: {},
    hoverValue: {},
    hoverPrice: {},
    isTouch: window.showcaseTouchDetected,

    init() {
      if (initialized) return
      initialized = true

      if (needsHydration) {
        product.hydrateProduct().then(() => {
          this.isHydrated = true
          this.cards = computeCards(product, config.blocks || [], defaultMaxVisible, config.sourceDiscountBadge || '')
        })
      }
      // else: already initialized synchronously above; nothing to do.
    },
    getCardImage(card, index) {
      return this.hoverImage[index] || card.image
    },

    getCardDescription(card, index) {
      return this.hoverValue[index] || card.description
    },

    getCardPrice(card, index) {
      return this.hoverPrice[index] ?? card.price
    },

    setHover(index, item, optionIndex) {
      this.hoverImage[index] = item.image
      const card = this.cards[index]
      const parts = card.baseOptions ? [...card.baseOptions] : []
      if (optionIndex >= 0 && optionIndex < parts.length) {
        parts[optionIndex] = item.value
      }
      this.hoverValue[index] = parts.filter(Boolean).join(' + ')

      const cardProduct = card.product || this.product
      const variant = cardProduct.variants?.find((v) => v.id === item.variantId)
      if (variant) {
        const bundle = card.configurationId
          ? cardProduct.getBundleWithSelectedVariant(card.configurationId, variant.options)
          : null
        const price = cardProduct.getFinalBundledPrice(variant, bundle)
        if (price != null) this.hoverPrice[index] = price
      }
    },

    clearHover(index) {
      delete this.hoverImage[index]
      delete this.hoverValue[index]
      delete this.hoverPrice[index]
    },

    goToVariant(item) {
      if (item.variantUrl) {
        window.location.href = item.variantUrl
      }
    },

    selectVariant(index, item, optionIndex) {
      if (this.isTouch) {
        const card = this.cards[index]
        if (!card) return

        // Persist the hovered preview so the card shows the selected variant.
        card.image = item.image || card.image
        card.url = item.variantUrl || card.url

        const parts = card.baseOptions ? [...card.baseOptions] : []
        if (optionIndex >= 0 && optionIndex < parts.length) {
          parts[optionIndex] = item.value
        }
        card.description = parts.filter(Boolean).join(' + ')

        // Try to update the price to the selected variant's price.
        // Use the card's product (per-block override) so a block whose product
        // differs from the source product still computes the right price.
        const cardProduct = card.product || this.product
        const variant = cardProduct.variants?.find((v) => v.id === item.variantId)
        if (variant) {
          const bundle = card.configurationId
            ? cardProduct.getBundleWithSelectedVariant(card.configurationId, variant.options)
            : null
          card.price = cardProduct.getFinalBundledPrice(variant, bundle)
        }
      } else {
        this.goToVariant(item)
      }
    },
  }
})

function getDefaultOptions(product) {
  if (!product.variants?.length) return ['', '', '']
  const v = product.variants.find((v) => v.available) || product.variants[0]
  return v.options || ['', '', '']
}

function resolveImageOverride(block) {
  if (!block.image_override) return null
  return block.image_override.src || block.image_override.url || block.image_override
}

function computeCards(product, blocks, defaultMaxVisible = 6, sourceDiscountBadge = '') {
  return blocks
    .map((block) => {
      // Per-block product override: when a block carries its own `product`,
      // build that card using the block's product data (title, options,
      // swatches, bundles, images, price, url). Falls back to the source
      // product when the block has no override.
      const cardProduct = block.product
        ? (() => {
            const p = ProductState({...block.product, isPDP: false})
            if (p.isSimple) p.bundles = []
            p.isSimple = !p.bundles.length
            return p
          })()
        : product

      const card = cardProduct.isSimple
        ? computeSimpleCard(cardProduct, block, defaultMaxVisible)
        : computeBundleCard(cardProduct, block, defaultMaxVisible)

      if (!card) return null

      // Surface per-card product + title so the template and reactive methods
      // (selectVariant, setHover, etc.) read from the right product instead
      // of always falling back to the source product.
      card.anchorId = block.anchor_id || ''
      card.product = cardProduct
      card.title = cardProduct.title

      // Append the per-product best-discount badge so it renders in the
      // card's tag list. Per-block badges (block.discount_badge) win over the
      // shared source product's badge.
      const discountBadge = (block.discount_badge || sourceDiscountBadge || '').trim()
      if (discountBadge) {
        card.labels = [...(card.labels || []), discountBadge]
      }

      return card
    })
    .filter(Boolean)
}

function buildFixedMap(block, product) {
  const swatchName = (block.swatch_option_name || product.collectionShowcaseSwatchesOptionName || '').trim()

  const numOptions = product.options?.length || 0
  const allIndices = Array.from({ length: numOptions }, (_, i) => i)
  const values = [...getDefaultOptions(product)]

  // Collect fixed option constraints from option_1/2/3 pairs. The first value
  // of each matching constraint pins the card variant.
  const constraints = []

  for (let i = 1; i <= 3; i++) {
    const name = (block[`option_${i}_fixed_option_name`] || '').trim()
    const value = (block[`option_${i}_fixed_option_value`] || '').trim()
    if (name && value) {
      constraints.push({ name, value })
    }
  }

  // No constraints -> all options become swatches
  if (constraints.length === 0) {
    return { values, fixed: [], variable: allIndices, fixedOptionName: '' }
  }

  const fixed = []
  const fixedNames = []

  constraints.forEach((constraint) => {
    const index = product.options?.findIndex(
      (opt) => opt.name.toLowerCase() === constraint.name.toLowerCase()
    ) ?? -1
    if (index < 0) return

    const valueMatches = product.variants.some(
      (variant) => variant.options[index] === constraint.value
    )
    if (!valueMatches) return

    if (!fixed.includes(index)) {
      fixed.push(index)
      values[index] = constraint.value
      fixedNames.push(product.options[index].name)
    }
  })

  // Default: all non-fixed options become swatches
  let variable = allIndices.filter((i) => !fixed.includes(i))

  // If user specified a specific option to show as swatches, restrict to that
  if (swatchName) {
    const swatchIndex = product.options?.findIndex(
      (opt) => opt.name.toLowerCase() === swatchName.toLowerCase()
    ) ?? -1
    if (swatchIndex >= 0) {
      variable = [swatchIndex]
    }
  }

  const fixedOptionName = fixedNames.join(' + ')

  return { values, fixed, variable, fixedOptionName }
}

function findVariant(product, targetOptions) {
  return (
    product.variants.find((v) =>
      v.options.every((opt, i) => opt === targetOptions[i])
    ) || null
  )
}

function findVariantPartial(product, values) {
  return (
    product.variants.find((v) => {
      return values.every((val, i) => {
        if (val === '') return true
        return v.options[i] === val
      })
    }) || null
  )
}

function buildSwatches(product, fixedValues, variableIndices, bundle = null, productUrl = '', configurationId = '', explicitValues = '') {
  const explicit = explicitValues
    ? [...new Set(explicitValues.split(',').map((s) => s.trim()).filter(Boolean))]
    : []

  return variableIndices.map((optionIndex) => {
    const option = product.options?.[optionIndex]
    const optionName = option?.name || ''

    const allValues = product.variants.map((v) => v.options[optionIndex])
    const availableValues = [...new Set(allValues)].filter(Boolean)

    // Reorder with explicit values first when this card has a single swatch option
    const isSingleOption = variableIndices.length === 1
    const swatchValues = isSingleOption && explicit.length
      ? explicit.filter((v) => availableValues.includes(v))
          .concat(availableValues.filter((v) => !explicit.includes(v)))
      : availableValues

    const baseUrl = configurationId
      ? `${productUrl}?configuration=${configurationId}&variant=`
      : `${productUrl}?variant=`

    const items = swatchValues.map((value) => {
      const target = [...fixedValues]
      target[optionIndex] = value
      const variant = findVariant(product, target) || findVariantPartial(product, target) || product.variants.find((v) => v.options[optionIndex] === value)
      const image = variant
        ? Shopify.ImagesByVariant(product.images, product, variant, bundle)[0]?.src
        : null

      // Swatch thumbnail from metafields (optionIndex is 1-based in swatches data)
      const swatchData = product.swatches?.find(
        (s) => s.optionIndex === optionIndex + 1 && s.value === value
      )
      const swatchImage = swatchData?.src
        ? Shopify.Image(swatchData.src, 50, 50, 'center')
        : null

      return {
        value,
        image: image || product.images[0]?.src,
        swatchImage,
        variantId: variant?.id,
        variantUrl: baseUrl + (variant?.id || ''),
      }
    })

    return { optionName, optionIndex, items, totalValues: swatchValues.length }
  })
}

function computeSimpleCard(product, block, defaultMaxVisible = 6) {
  const { values, fixed, variable, fixedOptionName } = buildFixedMap(block, product)

  const baseVariant = findVariantPartial(product, values) || product.variants[0]
  if (!baseVariant) return null

  const override = resolveImageOverride(block)
  const baseImage =
    override ||
    Shopify.ImagesByVariant(product.images, product, baseVariant, null)[0]?.src ||
    product.images[0]?.src

  const swatches = buildSwatches(product, values, variable, null, product.url, '', block.swatch_option_values)

  const description = fixed.length
    ? baseVariant.options.filter(Boolean).join(' + ')
    : ''

  return {
    label: block.label,
    labels: block.label
      ? block.label.split(',').map((s) => s.trim()).filter(Boolean)
      : [],
    image: baseImage,
    price: product.getFinalBundledPrice(baseVariant, null),
    description,
    url: `${product.url}?variant=${baseVariant.id}`,
    swatches,
    fixedOptionName,
    maxVisible: block.max_visible || defaultMaxVisible,
    baseOptions: baseVariant.options,
  }
}

function computeBundleCard(product, block, defaultMaxVisible = 6) {
  const bundleId = block.configuration_identifier || product.bundles[0]?.id
  const bundle =
    product.bundles.find(
      (b) => b.id === bundleId || b.title === bundleId
    ) || product.bundles[0]

  if (!bundle) return null

  // Debug: log bundle matching
  if (block.configuration_identifier && bundle.id !== block.configuration_identifier && bundle.title !== block.configuration_identifier) {
    console.warn(
      '[Showcase] Bundle identifier "%s" not found. Available bundles:',
      block.configuration_identifier,
      product.bundles.map((b) => ({ id: b.id, title: b.title }))
    )
  }

  // Apply fixed options like simple products
  const { values, fixed, variable, fixedOptionName } = buildFixedMap(block, product)

  const variant = findVariantPartial(product, values) || product.variants[0]
  if (!variant) return null

  // Use the actual variant's options for bundle resolution (not the potentially invalid values)
  const resolvedBundle = product.getBundleWithSelectedVariant(bundle.id, variant.options)

  const override = resolveImageOverride(block)
  const image =
    override ||
    Shopify.ImagesByVariant(
      product.images,
      product,
      variant,
      resolvedBundle
    )[0]?.src ||
    product.images[0]?.src

  const swatches = buildSwatches(product, values, variable, resolvedBundle, product.url, bundle.id, block.swatch_option_values)

  const description = fixed.length
    ? variant.options.filter(Boolean).join(' + ')
    : resolvedBundle.title

  return {
    label: block.label,
    labels: block.label
      ? block.label.split(',').map((s) => s.trim()).filter(Boolean)
      : [],
    image,
    price: product.getFinalBundledPrice(variant, resolvedBundle),
    description,
    url: `${product.url}?configuration=${bundle.id}&variant=${variant.id}`,
    swatches,
    fixedOptionName,
    maxVisible: block.max_visible || defaultMaxVisible,
    configurationId: bundle.id,
    baseOptions: variant.options,
  }
}

function updateStaticCardSwatch(swatch) {
  const card = swatch.closest('.product-config-card')
  const price = card?.querySelector('.product-config-card__price')
  const image = card?.querySelector('.product-config-card__image')
  const value = card?.querySelector('.product-config-card__config-value')
  const link = card?.querySelector('.product-config-card__link')

  if (price && swatch.dataset.price) {
    price.textContent = swatch.dataset.price
  }
  if (image) {
    image.src = swatch.dataset.preview || card.dataset.baseImage || image.src
  }
  if (link) {
    link.dataset.baseUrl ||= link.getAttribute('href') || ''
    if (swatch.dataset.variantUrl) link.href = swatch.dataset.variantUrl
  }
  if (value) {
    const options = JSON.parse(card.dataset.baseOptions || '[]')
    const optionIndex = Number(card.dataset.optionIndex)
    if (optionIndex >= 0 && optionIndex < options.length) {
      options[optionIndex] = swatch.dataset.value
    }
    value.textContent = options.filter(Boolean).join(' + ') || value.dataset.baseValue || ''
  }
}

function resetStaticCardSwatch(swatch) {
  const card = swatch.closest('.product-config-card')
  const price = card?.querySelector('.product-config-card__price')
  const image = card?.querySelector('.product-config-card__image')
  const value = card?.querySelector('.product-config-card__config-value')
  const link = card?.querySelector('.product-config-card__link')

  if (price && card.dataset.basePrice) {
    price.textContent = card.dataset.basePrice
  }
  if (image && card.dataset.baseImage) {
    image.src = card.dataset.baseImage
  }
  if (value) {
    value.textContent = value.dataset.baseValue || ''
  }
  if (link?.dataset.baseUrl) {
    link.href = link.dataset.baseUrl
  }
}

function initStaticCardSwatchPriceUpdates() {
  document.addEventListener('mouseover', (event) => {
    const swatch = event.target.closest?.('.product-config-card__swatch[data-price]')
    if (!swatch || swatch.contains(event.relatedTarget)) return
    updateStaticCardSwatch(swatch)
  })

  document.addEventListener('mouseout', (event) => {
    const swatch = event.target.closest?.('.product-config-card__swatch[data-price]')
    if (!swatch || swatch.contains(event.relatedTarget)) return
    resetStaticCardSwatch(swatch)
  })

  document.addEventListener('click', (event) => {
    if (!window.showcaseTouchDetected) return
    const swatch = event.target.closest?.('.product-config-card__swatch[data-price]')
    if (swatch) updateStaticCardSwatch(swatch)
  }, true)
}

const configurationTargetSelector = '[data-configuration-showcase-target]'
const configurationHighlightClass = 'is-configuration-highlighted'
const configurationHighlightDuration = 1200
const configurationScrollOffsetProperty = '--configuration-showcase-scroll-offset'
let highlightedConfiguration = null
let highlightedConfigurationAnimation = null
let configurationHighlightTimer = null
let pendingConfigurationObserver = null
let pendingConfigurationObserverTimer = null
let configurationOffsetObserver = null
let configurationScrollRequest = 0

function getConfigurationTarget(hash = window.location.hash) {
  if (!hash || hash === '#' || !hash.startsWith('#configuration-')) return null

  let id
  try {
    id = decodeURIComponent(hash.slice(1))
  } catch (error) {
    return null
  }

  const target = document.getElementById(id)
  return target?.matches(configurationTargetSelector) ? target : null
}

function getStickyOffset() {
  const announcement = document.getElementById('shopify-section-announcement-bar')
  const header = document.getElementById('shopify-section-header')

  const stickyBottom = [announcement, header].reduce((bottom, element) => {
    if (!element || window.getComputedStyle(element).display === 'none') return bottom
    return Math.max(bottom, element.getBoundingClientRect().bottom)
  }, 0)

  return Math.max(0, stickyBottom) + 16
}

function updateConfigurationScrollOffset() {
  document.documentElement.style.setProperty(
    configurationScrollOffsetProperty,
    `${getStickyOffset()}px`
  )
}

function highlightConfiguration(target) {
  if (highlightedConfiguration) {
    highlightedConfiguration.classList.remove(configurationHighlightClass)
  }
  highlightedConfigurationAnimation?.cancel()
  highlightedConfigurationAnimation = null
  if (configurationHighlightTimer) {
    window.clearTimeout(configurationHighlightTimer)
  }

  highlightedConfiguration = target
  target.classList.add(configurationHighlightClass)

  highlightedConfigurationAnimation = target.animate(
    [
      { opacity: 0.78, filter: 'brightness(0.92)', offset: 0 },
      { opacity: 1, filter: 'brightness(1)', offset: 0.25 },
      { opacity: 0.86, filter: 'brightness(0.95)', offset: 0.5 },
      { opacity: 1, filter: 'brightness(1)', offset: 0.75 },
      { opacity: 1, filter: 'brightness(1)', offset: 1 },
    ],
    {
      duration: configurationHighlightDuration,
      easing: 'ease-in-out',
    }
  )

  configurationHighlightTimer = window.setTimeout(() => {
    target.classList.remove(configurationHighlightClass)
    highlightedConfigurationAnimation = null
    if (highlightedConfiguration === target) highlightedConfiguration = null
  }, configurationHighlightDuration)
}

function highlightConfigurationWhenScrollSettles(target, requestId) {
  const startedAt = window.performance.now()
  let previousScrollY = window.scrollY
  let stableFrames = 0

  const checkScrollPosition = (now) => {
    if (requestId !== configurationScrollRequest) return

    const stickyOffset = getStickyOffset()
    const targetDelta = Math.abs(target.getBoundingClientRect().top - stickyOffset)
    const scrollDelta = Math.abs(window.scrollY - previousScrollY)
    previousScrollY = window.scrollY
    stableFrames = targetDelta < 1 && scrollDelta < 1 ? stableFrames + 1 : 0

    if (stableFrames >= 2 || now - startedAt >= 1600) {
      if (targetDelta >= 1) {
        const correctedTop = target.getBoundingClientRect().top + window.scrollY - stickyOffset
        window.scrollTo({ top: Math.max(0, correctedTop), behavior: 'auto' })
      }
      highlightConfiguration(target)
      return
    }

    window.requestAnimationFrame(checkScrollPosition)
  }

  window.requestAnimationFrame(checkScrollPosition)
}

function scrollToConfiguration(target, behavior = 'smooth') {
  const requestId = ++configurationScrollRequest
  const stickyOffset = getStickyOffset()
  document.documentElement.style.setProperty(configurationScrollOffsetProperty, `${stickyOffset}px`)
  const top = target.getBoundingClientRect().top + window.scrollY - stickyOffset

  window.scrollTo({
    top: Math.max(0, top),
    behavior,
  })
  highlightConfigurationWhenScrollSettles(target, requestId)
}

function stopWaitingForConfiguration() {
  pendingConfigurationObserver?.disconnect()
  pendingConfigurationObserver = null
  if (pendingConfigurationObserverTimer) {
    window.clearTimeout(pendingConfigurationObserverTimer)
    pendingConfigurationObserverTimer = null
  }
}

function revealConfigurationFromHash(behavior = 'smooth') {
  const target = getConfigurationTarget()
  if (target) {
    stopWaitingForConfiguration()
    scrollToConfiguration(target, behavior)
    return
  }

  if (!window.location.hash.startsWith('#configuration-') || pendingConfigurationObserver) return

  pendingConfigurationObserver = new MutationObserver(() => {
    const pendingTarget = getConfigurationTarget()
    if (!pendingTarget) return

    stopWaitingForConfiguration()
    scrollToConfiguration(pendingTarget, behavior)
  })
  pendingConfigurationObserver.observe(document.getElementById('MainContent') || document.body, {
    attributes: true,
    attributeFilter: ['id'],
    childList: true,
    subtree: true,
  })
  pendingConfigurationObserverTimer = window.setTimeout(stopWaitingForConfiguration, 10000)
}

function isCurrentPage(url) {
  const trimTrailingSlash = (path) => path.replace(/\/+$/, '') || '/'
  return (
    url.origin === window.location.origin &&
    trimTrailingSlash(url.pathname) === trimTrailingSlash(window.location.pathname)
  )
}

function initConfigurationDeepLinks() {
  updateConfigurationScrollOffset()
  window.addEventListener('resize', updateConfigurationScrollOffset)

  if ('ResizeObserver' in window) {
    configurationOffsetObserver = new ResizeObserver(updateConfigurationScrollOffset)
    const announcement = document.getElementById('shopify-section-announcement-bar')
    const header = document.getElementById('shopify-section-header')
    if (announcement) configurationOffsetObserver.observe(announcement)
    if (header) configurationOffsetObserver.observe(header)
  }

  document.addEventListener('click', (event) => {
    const link = event.composedPath().find(
      element => element instanceof HTMLAnchorElement && element.hasAttribute('href')
    )
    if (
      !link ||
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      link.target === '_blank' ||
      link.hasAttribute('download')
    ) {
      return
    }

    const url = new URL(link.href, window.location.href)
    if (!url.hash.startsWith('#configuration-') || !isCurrentPage(url)) return

    event.preventDefault()
    if (window.location.hash !== url.hash) {
      window.history.pushState(null, '', url.hash)
    }
    revealConfigurationFromHash()
  }, true)

  window.addEventListener('hashchange', () => revealConfigurationFromHash())
  window.addEventListener('pageshow', (event) => {
    updateConfigurationScrollOffset()
    if (event.persisted && window.location.hash.startsWith('#configuration-')) {
      window.requestAnimationFrame(() => revealConfigurationFromHash('auto'))
    }
  })

  if (window.location.hash.startsWith('#configuration-')) {
    const revealInitialConfiguration = () => {
      window.requestAnimationFrame(() => revealConfigurationFromHash('auto'))
    }
    if (document.readyState === 'complete') revealInitialConfiguration()
    else window.addEventListener('load', revealInitialConfiguration, { once: true })
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initStaticCardSwatchPriceUpdates()
    initConfigurationDeepLinks()
  }, { once: true })
} else {
  initStaticCardSwatchPriceUpdates()
  initConfigurationDeepLinks()
}
